import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSportsLang } from '../contexts/SportsLangContext';
import { uploadAndCreateSession, pollSessionStatus, getSessionReport, listSessions } from '../services/trackVisionService';
import {
  Activity, AlertTriangle, Clock, Video, ChevronRight, Loader2, Upload, Play,
  Timer, Footprints, Gauge, Target, FileVideo, Zap, ArrowRight, Trophy, Dumbbell,
} from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════
   Design Tokens
   ═══════════════════════════════════════════════════════════════ */
const T = {
  bg:         '#fafafa',
  card:       '#ffffff',
  text:       '#111111',
  textSoft:   '#6b7280',
  textMute:   '#9ca3af',
  border:     '#e5e7eb',
  accent:     '#111111',
  accentSoft: '#f3f4f6',
  success:    '#16a34a',
  successBg:  '#f0fdf4',
  warning:    '#f59e0b',
  warningBg:  '#fffbeb',
  error:      '#dc2626',
  errorBg:    '#fef2f2',
  info:       '#2563eb',
  infoBg:     '#eff6ff',
  purple:     '#7c3aed',
  purpleBg:   '#f5f3ff',
};

const PHASE_LABELS = {
  drive: 'Drive Phase',
  flight: 'Flight Phase',
  recovery: 'Recovery',
  block_start: 'Block Start',
  mid_stride: 'Mid-Stride',
};

const PHASE_COLORS = {
  drive: '#ef4444',
  flight: '#3b82f6',
  recovery: '#10b981',
  block_start: '#f59e0b',
  mid_stride: '#8b5cf6',
};

const LABELS_I18N = {
  pt: {
    subtitle: 'Análise Biomânica de Atletismo — Azure ML',
    competitionMode: 'Prova de Competição', competitionSub: 'Análise quantitativa',
    trainingMode: 'Prova de Treino', trainingSub: 'Análise biomânica completa',
    compBullets: [
      'Cadência, tempo de contato, tempo de voo',
      'Isolamento por raia (multi-atleta)',
      'Fases do sprint (start, drive, manutenção)',
      'Simetria e padrão de passada',
    ],
    trainBullets: [
      'Ângulos articulares (joelho, quadril, tornozelo, tronco)',
      'Simetria bilateral detalhada',
      'Classificação de marcha e postura',
      'Overlay técnico com landmarks',
    ],
    compBadge: 'Câmera lateral ou superior • Múltiplos atletas',
    trainBadge: 'Câmera próxima • Atleta isolado • Ângulo controlado',
    analyzeComp: 'Análise de Competição', analyzeTrain: 'Análise de Treino',
    quantBadge: 'Quantitativo', bioBadge: 'Biomânico',
    switchMode: '← Trocar modo',
    dropComp: 'Arraste o vídeo da prova de competição',
    dropTrain: 'Arraste o vídeo do treino (ângulo próximo)',
    unsupportedFormat: 'Formato não suportado. Use MP4, MOV ou AVI.',
    compConfig: 'Configuração de Prova', trainConfig: 'Configuração de Treino',
    eventLabel: 'Prova', laneLabel: 'Raia do Atleta', totalLanes: 'Total Raias', cameraLabel: 'Câmera',
    allLanes: 'Todas (sem filtro)', laneN: (n) => `Raia ${n}`,
    side: 'Lateral', top: 'Superior',
    eventTypeLabel: 'Tipo de Prova', focus: 'Foco',
    trainEventOptions: {
      sprint: 'Sprint (velocidade)', acceleration: 'Aceleração (saída de bloco)',
      gait: 'Marcha / Técnica de corrida', hurdles: 'Barreiras (técnica)',
      mid_distance: 'Meio-fundo', long_jump: 'Salto em distância',
      triple_jump: 'Salto triplo', high_jump: 'Salto em altura', pole_vault: 'Salto com vara',
    },
    cameraOptions: { side: 'Lateral (sagital)', diagonal: 'Diagonal (45°)', front: 'Frontal', rear: 'Posterior' },
    focusOptions: { full_body: 'Corpo inteiro', lower_body: 'Membros inferiores', upper_body: 'Tronco e braços' },
    trainTip: '💡 Posicione a câmera a 10-15m do atleta, perpendicular ao eixo de corrida para melhor medição de ângulos.',
    compEventGroups: { speed: 'Velocidade', midDistance: 'Meio-fundo', longDistance: 'Fundo', hurdles: 'Barreiras', relay: 'Revezamento' },
    compEventOptions: { '100mh': '100m c/ Barreiras (F)', '110mh': '110m c/ Barreiras (M)', '400mh': '400m c/ Barreiras', 'mid_distance': 'Meio-fundo (geral)', 'sprint': 'Sprint (geral)' },
    uploading: 'Enviando vídeo...', processing: 'Processando com TrackVision AI...',
    analyzing: 'Analisando...', analyze: 'Analisar',
    resultLabel: 'Resultado', compResultBadge: 'Competição', trainResultBadge: 'Treino',
    viewOverlay: 'Ver Overlay', details: 'Detalhes', newAnalysis: 'Nova Análise',
    cadenceLabel: 'Cadência', stridesLabel: 'Passadas', contactLabel: 'Contato',
    flightLabel: 'Voo', asymLabel: 'Assimetria',
    movQuality: 'Qualidade do Movimento',
    sprintPhases: 'Fases do Sprint',
    bioAngles: 'Ângulos Biomânicos', inRange: '% no range',
    ideal: 'Ideal', real: 'Real',
    recentSessions: 'Sessões Recentes', viewAll: 'Ver todas',
    errAnalysis: 'Erro na análise',
  },
  en: {
    subtitle: 'Athletics Biomechanical Analysis — Azure ML',
    competitionMode: 'Competition Race', competitionSub: 'Quantitative analysis',
    trainingMode: 'Training Session', trainingSub: 'Full biomechanical analysis',
    compBullets: [
      'Cadence, ground contact time, flight time',
      'Lane isolation (multi-athlete)',
      'Sprint phases (start, drive, maintenance)',
      'Symmetry and stride pattern',
    ],
    trainBullets: [
      'Joint angles (knee, hip, ankle, trunk)',
      'Detailed bilateral symmetry',
      'Gait and posture classification',
      'Technical overlay with landmarks',
    ],
    compBadge: 'Side or top camera • Multiple athletes',
    trainBadge: 'Close camera • Isolated athlete • Controlled angle',
    analyzeComp: 'Competition Analysis', analyzeTrain: 'Training Analysis',
    quantBadge: 'Quantitative', bioBadge: 'Biomechanical',
    switchMode: '← Change mode',
    dropComp: 'Drop competition race video here',
    dropTrain: 'Drop training video here (close angle)',
    unsupportedFormat: 'Unsupported format. Use MP4, MOV or AVI.',
    compConfig: 'Race Configuration', trainConfig: 'Training Configuration',
    eventLabel: 'Event', laneLabel: 'Athlete\'s Lane', totalLanes: 'Total Lanes', cameraLabel: 'Camera',
    allLanes: 'All (no filter)', laneN: (n) => `Lane ${n}`,
    side: 'Side', top: 'Top',
    eventTypeLabel: 'Event Type', focus: 'Focus',
    trainEventOptions: {
      sprint: 'Sprint (speed)', acceleration: 'Acceleration (block start)',
      gait: 'Gait / Running technique', hurdles: 'Hurdles (technique)',
      mid_distance: 'Middle distance', long_jump: 'Long jump',
      triple_jump: 'Triple jump', high_jump: 'High jump', pole_vault: 'Pole vault',
    },
    cameraOptions: { side: 'Side (sagittal)', diagonal: 'Diagonal (45°)', front: 'Front', rear: 'Rear' },
    focusOptions: { full_body: 'Full body', lower_body: 'Lower body', upper_body: 'Trunk and arms' },
    trainTip: '💡 Position camera 10-15m from athlete, perpendicular to running axis for best angle measurement.',
    compEventGroups: { speed: 'Speed', midDistance: 'Middle distance', longDistance: 'Long distance', hurdles: 'Hurdles', relay: 'Relay' },
    compEventOptions: { '100mh': '100m Hurdles (W)', '110mh': '110m Hurdles (M)', '400mh': '400m Hurdles', 'mid_distance': 'Middle distance (general)', 'sprint': 'Sprint (general)' },
    uploading: 'Uploading video...', processing: 'Processing with TrackVision AI...',
    analyzing: 'Analyzing...', analyze: 'Analyze',
    resultLabel: 'Result', compResultBadge: 'Competition', trainResultBadge: 'Training',
    viewOverlay: 'View Overlay', details: 'Details', newAnalysis: 'New Analysis',
    cadenceLabel: 'Cadence', stridesLabel: 'Strides', contactLabel: 'Contact',
    flightLabel: 'Flight', asymLabel: 'Asymmetry',
    movQuality: 'Movement Quality',
    sprintPhases: 'Sprint Phases',
    bioAngles: 'Biomechanical Angles', inRange: '% in range',
    ideal: 'Ideal', real: 'Real',
    recentSessions: 'Recent Sessions', viewAll: 'View all',
    errAnalysis: 'Analysis error',
  },
};

/* ═══════════════════════════════════════════════════════════════
   TrackVision Dashboard — Two Flows: Competition vs Training
   ═══════════════════════════════════════════════════════════════ */
const TrackVisionDashboard = () => {
  // ── State ──
  const [analysisMode, setAnalysisMode] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const { lang } = useSportsLang();
  const L = LABELS_I18N[lang] || LABELS_I18N.pt;

  // Competition-specific config
  const [targetLane, setTargetLane] = useState(0);
  const [nLanes, setNLanes] = useState(8);
  const [cameraAngle, setCameraAngle] = useState('auto');
  const [eventType, setEventType] = useState('100m');

  // Load recent sessions
  useEffect(() => {
    listSessions({ per_page: 5 }).then(r => setRecentSessions(r.data || [])).catch(() => {});
  }, []);

  // ── Handlers ──
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith('video/')) {
      setVideoFile(file);
      setError(null);
    } else {
      setError(L.unsupportedFormat);
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) { setVideoFile(file); setError(null); }
  }, []);

  const handleAnalyze = async () => {
    if (!videoFile || !analysisMode) return;
    setAnalyzing(true);
    setError(null);
    setResult(null);
    setUploadPct(0);
    setStatusText(L.uploading);

    const config = {
      analysis_mode: analysisMode,
      camera_angle: cameraAngle,
    };

    if (analysisMode === 'competition') {
      config.target_lane = targetLane;
      config.n_lanes = nLanes;
    }

    try {
      const { session_id } = await uploadAndCreateSession(
        videoFile, eventType, null,
        (pct) => setUploadPct(pct),
        config
      );
      setStatusText(L.processing);

      // Poll for completion
      await new Promise((resolve, reject) => {
        pollSessionStatus(session_id, {
          interval: 3000,
          onUpdate: (s) => {
            setStatusText(
              s.status === 'processing'
                ? `${L.processing.replace('...', '')}... ${s.progress_pct || 0}%`
                : `Status: ${s.status}`
            );
          },
          onComplete: (s) => {
            if (s.status === 'completed') resolve();
            else reject(new Error(s.error_message || `Session ${s.status}`));
          },
          onError: reject,
        });
      });

      // Fetch report
      const reportRes = await getSessionReport(session_id);
      console.log('[TrackVision Dashboard] reportRes:', reportRes);
      const rd = reportRes.data || reportRes || {};
      console.log('[TrackVision Dashboard] rd keys:', Object.keys(rd), 'cadence_spm:', rd.cadence_spm);
      // The /report endpoint returns the report JSON directly in .data
      // (no .reports[] wrapper — that was an old format)
      const report = rd.cadence_spm !== undefined ? rd : (rd.report_json || rd.reports?.[0]?.report_json || {});
      console.log('[TrackVision Dashboard] report.cadence_spm:', report.cadence_spm, 'report.ground_contact_ms:', report.ground_contact_ms);

      setResult({
        mode: analysisMode,
        session_id,
        gait: report.gait || null,
        cadence_spm: report.cadence_spm || report.avg_cadence_spm || 0,
        stride_length: report.stride_length || 0,
        ground_contact_ms: report.ground_contact_ms || 0,
        flight_time_ms: report.flight_time_ms || 0,
        asymmetry_score: report.asymmetry_score ?? report.asymmetry?.overall_asymmetry_pct ?? null,
        movement_quality_score: report.movement_quality_score || report.score?.overall_score || 0,
        total_strides: report.total_strides || 0,
        phases: (report.phases || rd.phases || []).map(p => ({
          phase: p.phase_name || p.phase,
          start_frame: p.start_frame,
          end_frame: p.end_frame,
          duration_s: p.duration_s || p.duration,
          confidence: p.confidence,
        })),
        angles: (report.angles || rd.angles || []).filter(a => a.joint_name || a.joint).map(a => ({
          joint: a.joint_name || a.joint,
          mean: a.mean_deg || a.mean,
          std: a.std_deg || a.std,
          min: a.min_deg || a.min,
          max: a.max_deg || a.max,
          optimal_min: a.optimal_min,
          optimal_max: a.optimal_max,
          in_range_pct: a.in_range_pct,
        })),
        overlay_url: rd.overlay_blob_url || report.overlay_blob_url,
        model_version: report.model_version || (rd.model_runs || [])[0]?.model_version,
        video_duration_s: report.video_duration_s || report.pipeline?.duration_s,
        video_fps: report.video_fps || report.pipeline?.fps,
      });

      // Refresh recent
      listSessions({ per_page: 5 }).then(r => setRecentSessions(r.data || [])).catch(() => {});
    } catch (err) {
      setError(err.message || L.errAnalysis);
    } finally {
      setAnalyzing(false);
      setStatusText('');
    }
  };

  const resetAnalysis = () => {
    setResult(null);
    setVideoFile(null);
    setAnalysisMode(null);
    setError(null);
  };

  // ── Render ──
  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6 space-y-6">

        {/* ── Header ── */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: T.accent }}>
            <Footprints size={20} style={{ color: '#ffffff' }} />
          </div>
          <div>
            <h1 className="text-2xl font-black" style={{ color: T.text }}>TrackVision</h1>
            <p className="text-xs" style={{ color: T.textSoft }}>
              {L.subtitle}
            </p>
          </div>
        </div>

        {/* ── Mode Selection (only when no mode selected) ── */}
        {!analysisMode && !result && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Competition Mode */}
            <button
              onClick={() => setAnalysisMode('competition')}
              className="bg-white rounded-xl border p-6 text-left hover:shadow-lg hover:border-gray-300 transition-all group"
              style={{ borderColor: T.border }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: T.infoBg }}>
                  <Trophy size={24} style={{ color: T.info }} />
                </div>
                <div>
                  <h2 className="text-lg font-black group-hover:text-blue-600" style={{ color: T.text }}>
                    {L.competitionMode}
                  </h2>
                  <p className="text-xs" style={{ color: T.textSoft }}>{L.competitionSub}</p>
                </div>
              </div>
              <ul className="space-y-2 text-xs" style={{ color: T.textSoft }}>
                {L.compBullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2"><Gauge size={12} style={{ color: T.info }} /> {b}</li>
                ))}
              </ul>
              <div className="mt-4 text-[10px] px-2 py-1 rounded-full inline-block" style={{ backgroundColor: T.infoBg, color: T.info }}>
                {L.compBadge}
              </div>
            </button>

            {/* Training Mode */}
            <button
              onClick={() => setAnalysisMode('training')}
              className="bg-white rounded-xl border p-6 text-left hover:shadow-lg hover:border-gray-300 transition-all group"
              style={{ borderColor: T.border }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: T.purpleBg }}>
                  <Dumbbell size={24} style={{ color: T.purple }} />
                </div>
                <div>
                  <h2 className="text-lg font-black group-hover:text-purple-600" style={{ color: T.text }}>
                    {L.trainingMode}
                  </h2>
                  <p className="text-xs" style={{ color: T.textSoft }}>{L.trainingSub}</p>
                </div>
              </div>
              <ul className="space-y-2 text-xs" style={{ color: T.textSoft }}>
                {L.trainBullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-2"><Target size={12} style={{ color: T.purple }} /> {b}</li>
                ))}
              </ul>
              <div className="mt-4 text-[10px] px-2 py-1 rounded-full inline-block" style={{ backgroundColor: T.purpleBg, color: T.purple }}>
                {L.trainBadge}
              </div>
            </button>
          </div>
        )}

        {/* ── Video Upload (shown after mode selected) ── */}
        {analysisMode && !result && (
          <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: T.border }}>
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: T.border }}>
              <div className="flex items-center gap-2">
                <Video size={16} style={{ color: T.accent }} />
                <span className="text-sm font-bold" style={{ color: T.text }}>
                  {analysisMode === 'competition' ? L.analyzeComp : L.analyzeTrain}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                  style={{
                    backgroundColor: analysisMode === 'competition' ? T.infoBg : T.purpleBg,
                    color: analysisMode === 'competition' ? T.info : T.purple,
                  }}>
                  {analysisMode === 'competition' ? L.quantBadge : L.bioBadge}
                </span>
              </div>
              <button onClick={() => { setAnalysisMode(null); setVideoFile(null); }}
                className="text-[11px] font-bold px-3 py-1 rounded-lg hover:bg-gray-100"
                style={{ color: T.textSoft }}>
                {L.switchMode}
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Drop zone */}
              <div
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  dragOver ? 'border-[#111] bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('tv-file-input').click()}
              >
                <input id="tv-file-input" type="file" accept="video/*" className="hidden" onChange={handleFileSelect} />
                {videoFile ? (
                  <div className="space-y-2">
                    <FileVideo size={32} className="mx-auto" style={{ color: T.success }} />
                    <p className="text-sm font-bold" style={{ color: T.text }}>{videoFile.name}</p>
                    <p className="text-xs" style={{ color: T.textMute }}>{(videoFile.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload size={32} className="mx-auto" style={{ color: T.textMute }} />
                    <p className="text-sm font-semibold" style={{ color: T.textSoft }}>
                      {analysisMode === 'competition' ? L.dropComp : L.dropTrain}
                    </p>
                    <p className="text-xs" style={{ color: T.textMute }}>MP4, MOV, AVI</p>
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 rounded-lg text-xs font-semibold flex items-center gap-2"
                  style={{ backgroundColor: T.errorBg, color: T.error }}>
                  <AlertTriangle size={14} /> {error}
                </div>
              )}

              {/* ── Competition Config ── */}
              {analysisMode === 'competition' && videoFile && (
                <div className="p-4 rounded-lg border" style={{ borderColor: T.border, backgroundColor: T.accentSoft }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Target size={14} style={{ color: T.info }} />
                    <span className="text-xs font-bold" style={{ color: T.text }}>{L.compConfig}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: T.textSoft }}>{L.eventLabel}</label>
                      <select value={eventType} onChange={(e) => setEventType(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: T.border }}>
                        <optgroup label={L.compEventGroups.speed}>
                          <option value="60m">60m</option>
                          <option value="100m">100m</option>
                          <option value="200m">200m</option>
                          <option value="sprint">{L.compEventOptions.sprint}</option>
                        </optgroup>
                        <optgroup label={L.compEventGroups.midDistance}>
                          <option value="400m">400m</option>
                          <option value="800m">800m</option>
                          <option value="mid_distance">{L.compEventOptions.mid_distance}</option>
                        </optgroup>
                        <optgroup label={L.compEventGroups.longDistance}>
                          <option value="1500m">1500m</option>
                          <option value="3000m">3000m</option>
                          <option value="5000m">5000m</option>
                          <option value="10000m">10000m</option>
                        </optgroup>
                        <optgroup label={L.compEventGroups.hurdles}>
                          <option value="100mh">{L.compEventOptions['100mh']}</option>
                          <option value="110mh">{L.compEventOptions['110mh']}</option>
                          <option value="400mh">{L.compEventOptions['400mh']}</option>
                        </optgroup>
                        <optgroup label={L.compEventGroups.relay}>
                          <option value="4x100m">4x100m</option>
                          <option value="4x400m">4x400m</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: T.textSoft }}>{L.laneLabel}</label>
                      <select value={targetLane} onChange={(e) => setTargetLane(Number(e.target.value))}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: T.border }}>
                        <option value={0}>{L.allLanes}</option>
                        {Array.from({ length: nLanes }, (_, i) => i + 1).map(lane => (
                          <option key={lane} value={lane}>{L.laneN(lane)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: T.textSoft }}>{L.totalLanes}</label>
                      <select value={nLanes} onChange={(e) => setNLanes(Number(e.target.value))}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: T.border }}>
                        <option value={6}>6</option>
                        <option value={8}>8</option>
                        <option value={9}>9</option>
                        <option value={10}>10</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: T.textSoft }}>{L.cameraLabel}</label>
                      <select value={cameraAngle} onChange={(e) => setCameraAngle(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: T.border }}>
                        <option value="auto">Auto</option>
                        <option value="side">{L.side}</option>
                        <option value="top">{L.top}</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Training Config ── */}
              {analysisMode === 'training' && videoFile && (
                <div className="p-4 rounded-lg border" style={{ borderColor: T.border, backgroundColor: T.purpleBg }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Dumbbell size={14} style={{ color: T.purple }} />
                    <span className="text-xs font-bold" style={{ color: T.text }}>{L.trainConfig}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: T.textSoft }}>{L.eventTypeLabel}</label>
                      <select value={eventType} onChange={(e) => setEventType(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: T.border }}>
                        {Object.entries(L.trainEventOptions).map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: T.textSoft }}>{L.cameraLabel}</label>
                      <select value={cameraAngle} onChange={(e) => setCameraAngle(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: T.border }}>
                        {Object.entries(L.cameraOptions).map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase mb-1 block" style={{ color: T.textSoft }}>{L.focus}</label>
                      <select className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: T.border }}>
                        {Object.entries(L.focusOptions).map(([v, label]) => (
                          <option key={v} value={v}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="mt-3 text-[10px]" style={{ color: T.textMute }}>{L.trainTip}</p>
                </div>
              )}

              {/* Analyze button */}
              <div className="flex justify-end">
                <button
                  onClick={handleAnalyze}
                  disabled={!videoFile || analyzing}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-40"
                  style={{ backgroundColor: T.accent }}>
                  {analyzing ? (
                    <><Loader2 size={16} className="animate-spin" /> {L.analyzing}{uploadPct > 0 && uploadPct < 100 ? ` (${uploadPct}%)` : ''}</>
                  ) : (
                    <><Play size={16} /> {L.analyze}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════ RESULTS ══════════════ */}
        {result && (
          <div className="space-y-4">
            {/* Action bar */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold" style={{ color: T.text }}>{L.resultLabel}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                  style={{
                    backgroundColor: result.mode === 'competition' ? T.infoBg : T.purpleBg,
                    color: result.mode === 'competition' ? T.info : T.purple,
                  }}>
                  {result.mode === 'competition' ? L.compResultBadge : L.trainResultBadge}
                </span>
              </div>
              <div className="flex gap-2">
                {result.overlay_url && (
                  <a href={result.overlay_url} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] font-bold px-3 py-1.5 rounded-lg border flex items-center gap-1"
                    style={{ borderColor: T.border, color: T.accent }}>
                    <Video size={12} /> {L.viewOverlay}
                  </a>
                )}
                <Link to={`/track-vision/sessions/${result.session_id}`}
                  className="text-[11px] font-bold px-3 py-1.5 rounded-lg border flex items-center gap-1"
                  style={{ borderColor: T.border, color: T.accent }}>
                  {L.details} <ChevronRight size={12} />
                </Link>
                <button onClick={resetAnalysis}
                  className="text-[11px] font-bold px-3 py-1.5 rounded-lg text-white"
                  style={{ backgroundColor: T.accent }}>
                  {L.newAnalysis}
                </button>
              </div>
            </div>

            {/* KPI Cards (both modes) */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <MetricCard icon={Gauge} label={L.cadenceLabel} value={result.cadence_spm} unit="spm" />
              <MetricCard icon={ArrowRight} label={L.stridesLabel} value={result.total_strides} unit="" />
              <MetricCard icon={Timer} label={L.contactLabel} value={result.ground_contact_ms} unit="ms" />
              <MetricCard icon={Zap} label={L.flightLabel} value={result.flight_time_ms} unit="ms" />
              <MetricCard icon={Activity} label={L.asymLabel}
                value={result.asymmetry_score != null ? `${result.asymmetry_score.toFixed(1)}` : '—'} unit="%" />
            </div>

            {/* Quality Score */}
            {result.movement_quality_score > 0 && (
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: T.border }}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold" style={{ color: T.text }}>{L.movQuality}</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black" style={{ color: T.accent }}>
                      {result.movement_quality_score.toFixed(0)}
                    </span>
                    <span className="text-xs" style={{ color: T.textMute }}>/100</span>
                  </div>
                </div>
                <div className="mt-2 h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{
                      width: `${result.movement_quality_score}%`,
                      backgroundColor: result.movement_quality_score >= 80 ? T.success :
                        result.movement_quality_score >= 60 ? T.warning : T.error,
                    }} />
                </div>
              </div>
            )}

            {/* Sprint Phases (if available) */}
            {result.phases.length > 0 && (
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: T.border }}>
                <div className="flex items-center gap-2 mb-4">
                  <Zap size={16} style={{ color: T.info }} />
                  <span className="text-sm font-bold" style={{ color: T.text }}>{L.sprintPhases}</span>
                </div>
                <div className="flex rounded-lg overflow-hidden h-10 mb-4">
                  {result.phases.map((p, i) => {
                    const totalFrames = result.phases[result.phases.length - 1]?.end_frame || 1;
                    const width = Math.max(2, ((p.end_frame - p.start_frame) / totalFrames) * 100);
                    return (
                      <div key={i} className="relative group" style={{
                        width: `${width}%`,
                        backgroundColor: PHASE_COLORS[p.phase] || '#94a3b8',
                      }}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[9px] font-bold text-white truncate px-1">
                            {PHASE_LABELS[p.phase] || p.phase}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-3">
                  {result.phases.map((p, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[10px]">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: PHASE_COLORS[p.phase] || '#94a3b8' }} />
                      <span style={{ color: T.textSoft }}>{PHASE_LABELS[p.phase] || p.phase}</span>
                      {p.duration_s && <span className="font-bold" style={{ color: T.text }}>{p.duration_s.toFixed(2)}s</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Biomechanical Angles (primarily for training mode) */}
            {result.angles.length > 0 && (
              <div className="bg-white rounded-xl border p-5" style={{ borderColor: T.border }}>
                <div className="flex items-center gap-2 mb-4">
                  <Target size={16} style={{ color: T.purple }} />
                  <span className="text-sm font-bold" style={{ color: T.text }}>{L.bioAngles}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {result.angles.map((a) => (
                    <div key={a.joint} className="rounded-lg p-3 border" style={{ borderColor: T.border }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold" style={{ color: T.text }}>{(a.joint || 'unknown').replace(/_/g, ' ')}</span>
                        {a.in_range_pct != null && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: a.in_range_pct >= 80 ? T.successBg : a.in_range_pct >= 50 ? T.warningBg : T.errorBg,
                              color: a.in_range_pct >= 80 ? T.success : a.in_range_pct >= 50 ? T.warning : T.error,
                            }}>
                            {a.in_range_pct.toFixed(0)}{L.inRange}
                          </span>
                        )}
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-black" style={{ color: T.text }}>{a.mean?.toFixed(1)}°</span>
                        {a.std != null && <span className="text-[10px]" style={{ color: T.textMute }}>±{a.std.toFixed(1)}°</span>}
                      </div>
                      {(a.optimal_min != null || a.optimal_max != null) && (
                        <div className="text-[10px] mt-1" style={{ color: T.textSoft }}>
                          {L.ideal}: {a.optimal_min}° – {a.optimal_max}° | {L.real}: {a.min?.toFixed(0)}° – {a.max?.toFixed(0)}°
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Recent Sessions ── */}
        {recentSessions.length > 0 && !result && !analyzing && (
          <div className="bg-white rounded-xl border p-5" style={{ borderColor: T.border }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock size={16} style={{ color: T.accent }} />
                <span className="text-sm font-bold" style={{ color: T.text }}>{L.recentSessions}</span>
              </div>
              <Link to="/track-vision/sessions" className="text-[11px] font-bold flex items-center gap-1" style={{ color: T.accent }}>
                {L.viewAll} <ChevronRight size={12} />
              </Link>
            </div>
            <div className="space-y-2">
              {recentSessions.map(s => (
                <Link key={s.id} to={`/track-vision/sessions/${s.id}`}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <FileVideo size={16} style={{ color: T.textMute }} />
                    <div>
                      <div className="text-xs font-bold" style={{ color: T.text }}>{s.video_filename || `Sessão #${s.id}`}</div>
                      <div className="text-[10px]" style={{ color: T.textMute }}>{s.event_type || 'auto'} • {s.created_at?.slice(0, 10)}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    s.status === 'completed' ? 'bg-green-50 text-green-600' :
                    s.status === 'processing' ? 'bg-blue-50 text-blue-600' :
                    s.status === 'failed' ? 'bg-red-50 text-red-600' :
                    'bg-gray-50 text-gray-500'
                  }`}>{s.status}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Metric Card ── */
const MetricCard = ({ icon: Icon, label, value, unit }) => (
  <div className="bg-white rounded-xl border p-4" style={{ borderColor: T.border }}>
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} style={{ color: T.textMute }} />
      <span className="text-[10px] font-bold uppercase" style={{ color: T.textSoft }}>{label}</span>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-xl font-black" style={{ color: T.text }}>{value != null && value !== '' ? value : '—'}</span>
      {unit && value != null && value !== '' ? <span className="text-[10px]" style={{ color: T.textMute }}>{unit}</span> : null}
    </div>
  </div>
);

export default TrackVisionDashboard;
